/**
 * КТО ПРОЧИТАЛ СООБЩЕНИЕ — маленькие аватары под пузырём.
 *
 * Решение Антона 18:45: «мне нравится система отметки маленьким аватаром
 * каждого участника», 18:58: «как только оно прочитанное — появился
 * логотип прочитавшего». Галочек нет вообще.
 *
 * ПОЧЕМУ ЭТО ЛУЧШЕ ГАЛОЧЕК В ГРУППЕ. Галочка отвечает «прочитано?»;
 * в комнате на 20 человек настоящий вопрос — КТО прочитал. «Все прочитали»
 * почти никогда не загорится (всегда есть отставший), «хотя бы один»
 * ничего не значит. Аватары отвечают на настоящий вопрос и работают
 * одинаково в личке и в группе — один механизм вместо двух.
 *
 * ---
 * ПОЧЕМУ ПЕРВАЯ ВЕРСИЯ РАБОТАЛА ЧЕРЕЗ РАЗ (Антон 18:57: «иногда отмечает,
 * иногда нет»). Я брал room.getUsersReadUpTo(event) — а он отдаёт только
 * тех, чья отметка стоит РОВНО на этом событии (проверено по
 * node_modules/matrix-js-sdk/lib/models/read-receipt.js:327 —
 * getReceiptsForEvent(event), точное совпадение event_id).
 *
 * Отметка собеседника почти никогда не попадает на моё последнее
 * сообщение: она уезжает на его собственный ответ, на реакцию, на
 * служебное событие — то есть на ЛЮБОЕ последнее событие комнаты. Тогда
 * под моим сообщением не было никого, хотя человек его прочитал.
 * Совпадало случайно — отсюда «иногда».
 *
 * ПРАВИЛЬНО: отметка означает «дочитал ДО СЮДА ВКЛЮЧИТЕЛЬНО». Значит надо
 * взять позицию отметки в таймлайне и считать прочитанными все события
 * на этой позиции и выше. Делаем через getEventReadUpTo(userId) для
 * каждого участника + индекс события в ленте.
 */
import { useEffect, useState } from "react";
import { RoomEvent, type MatrixClient, type Room } from "matrix-js-sdk";

/** Один читатель: id, отображаемое имя и буквы для аватара. */
export type Reader = {
  userId: string;
  name: string;
  initials: string;
  /** mxc аватара, если есть у участника. null — кружок с буквами. */
  avatarMxc: string | null;
};

/**
 * Докуда дочитал каждый участник.
 *
 * `index` — позиция его отметки в текущем таймлайне. Сообщение считается
 * прочитанным этим человеком, если позиция сообщения <= index.
 */
export type ReadState = {
  /** Порядковый номер события в ленте: eventId -> index. */
  indexOf: Map<string, number>;
  /** Читатели по позиции их отметки (я исключён). */
  readers: { reader: Reader; index: number }[];
};

function initialsOf(name: string): string {
  return name.replace(/^[#!@]/, "").slice(0, 2).toUpperCase();
}

export function useReadReceipts(client: MatrixClient, room: Room): ReadState {
  const [state, setState] = useState<ReadState>({
    indexOf: new Map(),
    readers: [],
  });

  useEffect(() => {
    const me = client.getUserId();

    const recompute = () => {
      const events = room.getLiveTimeline().getEvents();
      const indexOf = new Map<string, number>();
      events.forEach((ev, i) => {
        const id = ev.getId();
        if (id) indexOf.set(id, i);
      });

      const readers: { reader: Reader; index: number }[] = [];
      // Идём по УЧАСТНИКАМ, а не по событиям: у каждого человека ровно
      // одна отметка «дочитал до», и она может стоять на любом событии.
      for (const member of room.getJoinedMembers()) {
        if (member.userId === me) continue; // «я прочитал своё» — шум
        const upTo = room.getEventReadUpTo(member.userId);
        if (!upTo) continue;
        const index = indexOf.get(upTo);
        // Отметка может указывать на событие, которого ещё нет в нашем
        // куске таймлайна (мы не догрузили ту часть). Тогда позиция
        // неизвестна — молча пропускаем, не выдумывая.
        if (index === undefined) continue;
        readers.push({
          reader: {
            userId: member.userId,
            name: member.name,
            initials: initialsOf(member.name),
            avatarMxc: member.getMxcAvatarUrl() ?? null,
          },
          index,
        });
      }
      setState({ indexOf, readers });
    };

    recompute();
    // Room.receipt — единственный источник обновления отметок. Без
    // подписки читатели появлялись бы с задержкой в одно сообщение:
    // отметки приходят ОТДЕЛЬНЫМ событием, не вместе с сообщением.
    const onReceipt = (_ev: unknown, r: Room) => {
      if (r.roomId === room.roomId) recompute();
    };
    client.on(RoomEvent.Receipt, onReceipt);
    return () => {
      client.removeListener(RoomEvent.Receipt, onReceipt);
    };
  }, [client, room]);

  return state;
}

/**
 * РАССТАВИТЬ ЧИТАТЕЛЕЙ ПО СООБЩЕНИЯМ — «кто где остановился».
 *
 * Постановка Антона 19:10: «Динусси просмотрела моё предпоследнее
 * сообщение, Альперен — последнее. Как только Альперен посмотрел
 * последнее, метка слетает. Это неправильно. Ты должен показывать, кто
 * до какого сообщения просмотрел, кто остановился. Если все дошли до
 * низа — все переезжают на последнюю метку. Если один прочитал до
 * середины, второй до конца — метки должны остаться.»
 *
 * ЧТО БЫЛО НЕ ТАК. Первая версия рисовала аватары только под ПОСЛЕДНИМ
 * моим сообщением с читателями. Любой, кто дочитал до конца, забирал
 * туда всех остальных: отставший исчезал из ленты, хотя он никуда не
 * двигался. Информация «докуда дошёл каждый» терялась полностью.
 *
 * КАК ПРАВИЛЬНО. У каждого человека ровно одна позиция — его отметка.
 * Он показывается ОДИН раз, под тем моим сообщением, на котором
 * остановился. Дошли все до низа — все аватары естественно собрались
 * внизу; кто-то отстал — его лицо осталось там, где он стоит.
 *
 * Почему «ближайшее моё сообщение НЕ ВЫШЕ отметки»: отметка может
 * указывать на чужое сообщение, реакцию или служебное событие. Ставить
 * аватар туда некуда — привязываем к последнему моему сообщению, которое
 * человек точно уже прочитал.
 */
export function assignReaders(
  state: ReadState,
  myEventIds: string[],
): Map<string, Reader[]> {
  // Мои сообщения с их позициями, по возрастанию.
  const mine = myEventIds
    .map((id) => ({ id, index: state.indexOf.get(id) }))
    .filter((m): m is { id: string; index: number } => m.index !== undefined)
    .sort((a, b) => a.index - b.index);

  const out = new Map<string, Reader[]>();
  if (mine.length === 0) return out;

  for (const { reader, index } of state.readers) {
    // Последнее моё сообщение на позиции отметки или выше.
    let target: string | null = null;
    for (const m of mine) {
      if (m.index <= index) target = m.id;
      else break;
    }
    // Отметка стоит ВЫШЕ всех моих сообщений — человек не дочитал ни до
    // одного из них. Показывать нечего, и выдумывать позицию нельзя.
    if (!target) continue;
    const list = out.get(target);
    if (list) list.push(reader);
    else out.set(target, [reader]);
  }
  return out;
}
